import { useState, useRef, useEffect } from "react";
import { LinearProgress, Snackbar, Stack, Typography } from "@mui/joy";
import { bytesToMegabytes } from "../../util/objectUtil";

export default function FileLoadProgress({
    loadedBytes,
    totalBytes,
}: {
    loadedBytes: number;
    totalBytes: number;
}) {
    const [display, setDisplay] = useState({
        loadedMB: bytesToMegabytes(loadedBytes),
        totalMB: bytesToMegabytes(totalBytes),
        percent: totalBytes > 0 ? Math.floor((loadedBytes / totalBytes) * 100) : 0,
    });

    const lastPercent = useRef(display.percent);

    useEffect(() => {
        const newPercent = Math.floor((loadedBytes / totalBytes) * 100);
        if (newPercent !== lastPercent.current) {
            lastPercent.current = newPercent;
            setDisplay({
                loadedMB: bytesToMegabytes(loadedBytes),
                totalMB: bytesToMegabytes(totalBytes),
                percent: newPercent,
            });
        }
    }, [loadedBytes, totalBytes]);

    // NB - we delay the display of the progress bar until we have loaded at least 0.5% of the file to avoid
    // flashing it on the screen for a short period of time when loading from local filesystem.
    const show = display.percent < 100 && display.percent >= 0.5;

    return (
        <Snackbar open={show} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
            <Stack spacing={1} sx={{ flex: 1 }}>
                <LinearProgress determinate={totalBytes > 0} value={display.percent} />
                <Stack direction="row" justifyContent="space-between">
                    <Typography level="body-xs">Downloading…</Typography>
                    <Typography level="body-xs" sx={{ fontSize: 10, fontFamily: "monospace" }}>
                        {display.loadedMB}/{display.totalMB} MB
                    </Typography>
                </Stack>
            </Stack>
        </Snackbar>
    );
}
